import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath } from '../utils/paths';
import { formatBlogDate } from '../utils/blogUtils';
import BlogShareBar from './BlogShareBar';
import './ClubBlog.css';

const ClubBlogAdmin = () => {
  const { id: clubId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [club, setClub] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unpublishingPostId, setUnpublishingPostId] = useState(null);

  const loadData = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError('');
      const [clubData, postsData] = await Promise.all([
        api.getClubById(clubId),
        api.getClubPosts(clubId, user.id),
      ]);
      setClub(clubData);
      setPosts(Array.isArray(postsData) ? postsData : []);
    } catch (err) {
      setError(err.message || 'Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  }, [clubId, user?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUnpublish = async (post) => {
    if (!user?.id) return;
    if (!window.confirm(`Unpublish "${post.title}"? It will be hidden from the public blog.`)) {
      return;
    }
    try {
      setUnpublishingPostId(post.id);
      setError('');
      await api.updateClubPost(clubId, post.id, {
        title: post.title,
        excerpt: post.excerpt || '',
        body: post.body,
        coverImageUrl: post.coverImageUrl || null,
        photoUrls: post.photoUrls || [],
        referenceLinks: post.referenceLinks || [],
        status: 'DRAFT',
      }, user.id);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to unpublish post');
    } finally {
      setUnpublishingPostId(null);
    }
  };

  return (
    <div className="club-blog-admin card">
      <div className="club-blog-admin__header">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate(soccerPath(`/clubs/${clubId}/admin`))}
        >
          ← Back to admin
        </button>
        <h2>Club blog{club ? ` — ${club.name}` : ''}</h2>
        <Link to={soccerPath(`/clubs/${clubId}/admin/blog/new`)} className="btn btn-primary">
          New post
        </Link>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading && <p>Loading posts…</p>}

      {!loading && posts.length === 0 && (
        <p className="club-blog-admin__empty">
          No posts yet.{' '}
          <Link to={soccerPath(`/clubs/${clubId}/admin/blog/new`)}>Write your first post</Link>
        </p>
      )}

      {!loading && posts.length > 0 && (
        <ul className="club-blog-admin__list">
          {posts.map((post) => (
            <li key={post.id} className="club-blog-admin__item">
              <div>
                <strong>{post.title}</strong>
                <span className={`club-blog-admin__badge club-blog-admin__badge--${post.status.toLowerCase()}`}>
                  {post.status}
                </span>
                <p className="club-blog-admin__meta">
                  {post.publishedAt
                    ? `Published ${formatBlogDate(post.publishedAt)}`
                    : `Updated ${formatBlogDate(post.updatedAt || post.createdAt)}`}
                </p>
              </div>
              <div className="club-blog-admin__actions">
                {post.status === 'PUBLISHED' && (
                  <>
                    <Link to={`/blog/${post.slug}`} className="btn btn-secondary" target="_blank" rel="noopener noreferrer">
                      View
                    </Link>
                    <BlogShareBar post={post} compact />
                  </>
                )}
                {post.status === 'PUBLISHED' && (
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={() => handleUnpublish(post)}
                    disabled={unpublishingPostId === post.id}
                  >
                    {unpublishingPostId === post.id ? 'Unpublishing…' : 'Unpublish'}
                  </button>
                )}
                <Link
                  to={soccerPath(`/clubs/${clubId}/admin/blog/${post.id}/edit`)}
                  className="btn btn-primary"
                >
                  Edit
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ClubBlogAdmin;
